import React, {
  useState,
  useEffect,
  useRef,
} from "react";

interface Coord {
  x: number;
  y: number;

}

export default function DraggableDivider({
  defaultRatio=0.7,
  topHalf,
  bottomHalf,
  orientation="auto",
} : {
  defaultRatio?: number,
  topHalf: React.ReactNode,
  bottomHalf: React.ReactNode,
  orientation?: string,
}) {

  // sm	40rem (640px)
  // md	48rem (768px)
  // lg	64rem (1024px)
  // xl	80rem (1280px)
  // 2xl	96rem (1536px)

  const outerDivRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  // ref for intermediate values
  const sizesRef = useRef({width: -1, height: -1});

  useEffect(() => {
    if (!outerDivRef.current) {
      // we do not initialize the observer unless the ref has
      // been assigned
      return;
    }

    // we also instantiate the resizeObserver and we pass
    // the event handler to the constructor
    const resizeObserver = new ResizeObserver(() => {
      if (!outerDivRef.current || !sizesRef.current) {
        return
      }

      if(outerDivRef.current.offsetWidth !== sizesRef.current.width) {
        sizesRef.current.width = outerDivRef.current.offsetWidth;
        setWidth(outerDivRef.current.offsetWidth); 
      }
      if(outerDivRef.current.offsetHeight !== sizesRef.current.height) {
        sizesRef.current.height = outerDivRef.current.offsetHeight;
        setHeight(outerDivRef.current.offsetHeight);
      }
    });
    
    // the code in useEffect will be executed when the component
    // has mounted, so we are certain observedDiv.current will contain
    // the div we want to observe
    resizeObserver.observe(outerDivRef.current);


    // if useEffect returns a function, it is called right before the
    // component unmounts, so it is the right place to stop observing
    // the div
    return (() => {
      resizeObserver.disconnect();
    });
  }, [])

  const windowSize = {
    width: width,
    height: height,
  }

  const isHorizontal = orientation === "auto"
    ? windowSize.width > 768
    : orientation === "vertical"
      ? false
      : true

  const [isDragging, setIsDragging] = useState(false)
  const [ratio, setRatio] = useState(defaultRatio);

  const [mouseRatio, setMouseRatio] = useState<Coord>({
    x: 0.5,
    y: 0.5,
  })

  function setNewRatio(pos: Coord) {
    if (isHorizontal) {
      setRatio(pos.x)
    } else {
      setRatio(pos.y)
    }
  }

  useEffect(() => {
    if (isDragging) {
      setNewRatio(mouseRatio)
    }
  }, [isDragging, mouseRatio])

  return (
    <div className={`
      h-full w-full flex ${ isHorizontal ? "flex-row": "flex-col" }
      ${isDragging ? "cursor-move" : ""}`}
      onPointerUp={() => setIsDragging(false)}
      onMouseMove={(e) => {
        if (!windowSize.width || !windowSize.height) {
          return;
        }
        setMouseRatio({
          x: e.clientX / windowSize.width, y: e.clientY / windowSize.height
        })
      }}
      ref={outerDivRef}
    >
      <div className="relative" style={{
        height: windowSize.height * (isHorizontal ? 1 : ratio),
        width: windowSize.width * (isHorizontal ? ratio : 1),
      }}>
        {topHalf}
      </div>

      <div
        className={`relative hover:cursor-move ${isHorizontal ? "h-full w-[1px]" : "h-[1px] w-full"}`}
        onPointerDown={() => setIsDragging(true)}
      >
        <div className={`w-full h-full
          ${isHorizontal ? "hover:-mx-5" : "hover:-my-5"}
          ${isDragging ? "p-5" : "hover:p-5"}
        `}/>
      </div>

      <div style={{
        height: windowSize.height * (isHorizontal ? 1 : 1-ratio) - (isHorizontal ? 0 : 1),
        width: windowSize.width * (isHorizontal ? 1-ratio : 1) - (isHorizontal ? 1 : 0),
      }}>
        {bottomHalf}
      </div>
    </div>
  )
}
